import { Component } from '@theme/component';

/** Filterable editable portfolio with a native modal image viewer. */
class ProjectPortfolio extends Component {
  #abort = new AbortController();
  #items = [];
  #photos = [];
  #index = 0;
  #trigger;
  #swipe;
  #dialog;
  #category = 'all';
  #limit = 12;

  connectedCallback() { super.connectedCallback(); this.#setup(); }
  updatedCallback() { super.updatedCallback(); this.#setup(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#abort.abort(); this.#dialog?.close(); }

  #setup() {
    this.#abort.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#items = [...this.querySelectorAll('[data-portfolio-item]')].sort((a, b) => Number(a.dataset.sortOrder || 0) - Number(b.dataset.sortOrder || 0));
    const grid = this.querySelector('.ns-portfolio__grid');
    if (grid) for (const item of this.#items) grid.append(item);
    this.#dialog = this.querySelector('[data-portfolio-dialog]');
    if (!(this.#dialog instanceof HTMLDialogElement)) return;
    this.#refresh();
    this.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const filter = event.target.closest('[data-filter]');
      if (filter) {
        this.#category = filter.dataset.filter;
        this.#limit = 12;
        for (const button of this.querySelectorAll('[data-filter]')) button.setAttribute('aria-pressed', String(button === filter));
        this.#refresh();
      }
      if (event.target.closest('[data-load-more]')) {
        const firstNew = this.#matching()[this.#limit];
        this.#limit += 12;
        this.#refresh();
        firstNew?.querySelector('a')?.focus({ preventScroll: true });
      }
      const link = event.target.closest('[data-portfolio-open]');
      if (link instanceof HTMLAnchorElement) {
        event.preventDefault();
        this.#trigger = link;
        this.#photos = this.#matching().flatMap(item => {
          const p = item.dataset;
          const photos = [{ src: p.full, title: p.title, description: p.description, link: p.link, id: p.portfolioItem }];
          if (p.secondary) photos.push({ src: p.secondary, title: p.secondaryAlt || p.title, description: p.description, link: p.link, id: p.portfolioItem });
          return photos;
        });
        this.#index = Math.max(0, this.#photos.findIndex(photo => photo.id === link.dataset.portfolioOpen));
        this.#show();
        if (!this.#dialog.open) this.#dialog.showModal();
        this.#dialog.querySelector('[data-viewer-close]')?.focus();
      }
      if (event.target.closest('[data-viewer-close]')) this.#dialog.close();
      if (event.target.closest('[data-viewer-prev]')) this.#move(-1);
      if (event.target.closest('[data-viewer-next]')) this.#move(1);
    }, { signal });
    this.#dialog.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.#move(event.key === 'ArrowLeft' ? -1 : 1); }
    }, { signal });
    this.#dialog.addEventListener('close', () => { this.#trigger?.focus({ preventScroll: true }); }, { signal });
    const canvas = this.querySelector('[data-viewer-canvas]');
    canvas?.addEventListener('touchstart', event => { this.#swipe = event.touches[0]?.clientX; }, { passive: true, signal });
    canvas?.addEventListener('touchend', event => {
      const x = event.changedTouches[0]?.clientX;
      if (typeof this.#swipe === 'number' && typeof x === 'number' && Math.abs(x - this.#swipe) > 45) this.#move(x < this.#swipe ? 1 : -1);
      this.#swipe = undefined;
    }, { passive: true, signal });
    canvas?.addEventListener('touchcancel', () => { this.#swipe = undefined; }, { signal });
  }

  #matching() { return this.#items.filter(item => this.#category === 'all' || item.dataset.category === this.#category); }
  #refresh() {
    const matching = this.#matching();
    for (const item of this.#items) item.hidden = !matching.includes(item) || matching.indexOf(item) >= this.#limit;
    const more = this.querySelector('[data-load-more]');
    if (more) more.hidden = matching.length <= this.#limit;
    const status = this.querySelector('[data-portfolio-status]');
    if (status) status.textContent = `${Math.min(this.#limit, matching.length)} / ${matching.length} ${status.dataset.countLabel}`;
  }

  #move(direction) { this.#index = (this.#index + direction + this.#photos.length) % this.#photos.length; this.#show(); }
  #show() {
    const photo = this.#photos[this.#index];
    if (!photo || !this.#dialog) return;
    const image = this.#dialog.querySelector('[data-viewer-image]');
    image.src = photo.src; image.alt = photo.title;
    this.#dialog.querySelector('[data-viewer-title]').textContent = photo.title;
    this.#dialog.querySelector('[data-viewer-description]').textContent = photo.description || '';
    this.#dialog.querySelector('[data-viewer-count]').textContent = `${this.#index + 1} / ${this.#photos.length}`;
    const link = this.#dialog.querySelector('[data-viewer-link]');
    link.hidden = !photo.link;
    if (photo.link && photo.link.startsWith('/products/')) link.setAttribute('href', photo.link);
    else { link.hidden = true; link.removeAttribute('href'); }
  }
}
if (!customElements.get('project-portfolio')) customElements.define('project-portfolio', ProjectPortfolio);
